import { useLocation, useSearchParams } from "react-router-dom";
import { defaultConfig } from "../../../hooks/useSearch";
import { RESERVATION_FILTER_KEYS } from "./reservationFilters";

const ALL_KEYS = Object.values(RESERVATION_FILTER_KEYS);

// Reads and writes the reservations list's filters in the URL. Every write is a new result set,
// so it also drops the page number and the list returns to page 1. `keys` is what "any filter
// applied" and "clear all" look at; another list (the eSIM reservations) passes its own.
const useFilterParams = (keys: string[] = ALL_KEYS) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();

  const get = (key: string) => searchParams.get(key) || "";

  const update = (apply: (params: URLSearchParams) => void) => {
    const params = new URLSearchParams(searchParams);
    apply(params);
    params.delete(defaultConfig.paginationSelector);
    setSearchParams(params, { replace: true, state });
  };

  // A falsy value removes the key, so "any" never lingers in the URL.
  const set = (entries: Record<string, string | undefined>) =>
    update((params) =>
      Object.entries(entries).forEach(([key, value]) =>
        value ? params.set(key, value) : params.delete(key)
      )
    );

  const hasFilters = keys.some((key) => searchParams.has(key));
  const clearAll = () => update((params) => keys.forEach((key) => params.delete(key)));

  return { searchParams, get, set, hasFilters, clearAll };
};

export default useFilterParams;
